/**
 * Whether the app running in this window is the one the server is serving
 * (D-520).
 *
 * <b>Written for the installed app, which cannot reload itself.</b> A browser
 * tab recovers on the next refresh. A Home Screen web app (D-518) is resumed
 * for days and has no address bar and no reload button.
 *
 * ⚠ This went on to say a deploy leaves it asking for lazy chunks that now 404.
 * <b>There is no code splitting in this build</b>, so that cannot happen
 * (D-902). Why the prompt is worth having anyway lives on `NewVersionBanner`,
 * which is the one place it is written down now.
 */

/** What the server says it is serving. */
export async function fetchBuild(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch('/api/version', { signal, cache: 'no-store' })
    if (!res.ok) return null

    const body = (await res.json()) as { build?: string }
    return typeof body.build === 'string' ? body.build : null
  } catch {
    /*
     * <b>A failed check is never a new version.</b> Every database-backed
     * endpoint refuses for ~75 seconds after a deploy (D-33) and a phone loses
     * signal constantly — treating either as "something changed" would put a
     * reload prompt in front of people at exactly the moment reloading is most
     * likely to land them on a broken app.
     */
    return null
  }
}

/**
 * Whether a newly-read build means the running app is out of date.
 *
 * Pure so the rule can be tested without a network: the interesting cases are
 * all about what should <em>not</em> prompt.
 */
export function isStale(firstSeen: string | null, latest: string | null): boolean {
  // Nothing to compare against yet, or the check failed. Never prompt.
  if (firstSeen === null || latest === null) return false

  /*
   * A host with no built SPA reports a constant, which is development — Vite
   * serves the app there and does its own reloading. Comparing would be
   * comparing two identical strings anyway; this says why, so nobody later
   * "fixes" it into something that prompts on every poll.
   */
  return firstSeen !== latest
}
